import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LaasOption } from 'src/app/core/models/laas-option';
import { PageInfo } from 'src/app/core/models/page-info';
import { AllocationConstraintInboundRequest } from 'src/app/core/models/transport-operation/query-allocation-constraint-inbound-request';
import { AddAllocationConstraintInboundRequest } from 'src/app/core/models/transport-operation/add-allocation-constraint-inbound-request';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { deepCopy, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { AddressMaintainanceService } from 'src/app/features/setting/service/address-maintainance.service';
import { DeliveryOrderService } from 'src/app/features/transport-operation/services/delivery-order.service';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { CalendarComponent } from 'src/app/shared/components/calendar/calendar.component';
import { SelectItem } from 'primeng/api';
import { Table } from 'typeorm';
import { format } from 'date-fns';

@Component({
  selector: 'laas-allocation-constraint-inbound',
  templateUrl: './allocation-constraint-inbound.component.html',
  styleUrls: ['./allocation-constraint-inbound.component.scss']
})
export class AllocationConstraintInboundComponent  extends SessionBaseComponent implements OnInit {

  @ViewChild('AllocateConditionTable') dataTable!: Table;
  @ViewChild('addFormStartDate') startDateCalendar!: CalendarComponent;
  @ViewChild('addFormEndDate') endDateCalendar!: CalendarComponent;

  readonly viewPermission = PermissionConstants.ViewAllocationConstraintInbound;
  readonly addPermission = PermissionConstants.AddAllocationConstraintInbound;

  currentLang: string = this.laasSetting.language;
  isSearchFormOpen: boolean = true;
  isLoading: boolean = false;
  submitted: boolean = false;
  addFormSubmitted: boolean = false;
  canAddPermission: boolean = false;

  searchForm: FormGroup;
  addForm: FormGroup;
  searchParams: AllocationConstraintInboundRequest = new AllocationConstraintInboundRequest();

  brandOptions: SelectItem[] = [];
  storerOption: SelectItem[] = [];
  checkTypeOption: SelectItem[] = [];
  addCheckTypeOption: SelectItem[] = [];
  checkConditionOptionMap = {
    'VENDOR_LOT': [
      { label: '=', value: 'IN' },
      { label: '!=', value: 'NOT IN' },
    ],
    'DATE_CODE': [
      { label: '>', value: '>' },
      { label: '<', value: '<' },
    ]
  };

  commonOption: any[];

  pageInfo: PageInfo;
  sortInfo: [{ property: string; direction: number }] | null;

  tableColumns: any[];
  showTableColumns: any[];

  tableValue: any[] = [];

  selectedRowId: any;
  docTypeOptions: LaasOption[] = [];

  showDialog: boolean = false;
  checkValue1Invalid: boolean = false;

  constructor(
      injector: Injector,
      private formBuilder: FormBuilder,
      private DropdownOptionsService: DropdownOptionsService,
      private AddressMaintainanceService: AddressMaintainanceService,
      private DeliveryOrderService: DeliveryOrderService,
  ) {
    super(injector);

    this.tableColumns= [
      {field: 'storer', header: 'AllocationConstraint.Column.storer', sort: 'true'},
      {field: 'consigneeKey', header: 'AllocationConstraint.Column.consigneeKey', sort: 'true'},
      {field: 'customerName', header: 'AllocationConstraint.Column.customerName', sort: false},
      {field: 'brand', header: 'AllocationConstraint.Column.brand', sort: 'true'},
      {field: 'sku', header: 'AllocationConstraint.Column.sku', sort: 'true'},
      {field: 'checkType', header: 'AllocationConstraint.Column.checkType', sort: 'true'},
      {field: 'checkCondition', header: 'AllocationConstraint.Column.checkCondition', sort: 'true'},
      {field: 'checkValue1', header: 'AllocationConstraint.Column.checkValue1', sort: 'true'},
      {field: 'startDate', header: 'AllocationConstraint.Column.effectiveDt', dataType: 'date', sort: 'true'},
      {field: 'endDate', header: 'AllocationConstraint.Column.expirationDt', dataType: 'date', sort: 'true'},
      {field: 'activeFlag', header: 'AllocationConstraint.Column.activeFlag', sort: 'true'},
      {field: 'createdDate', header: 'AllocationConstraint.Column.createDt', dataType: 'date', sort: 'true'},
      {field: 'createdBy', header: 'AllocationConstraint.Column.creater', sort: 'true'},
      {field: 'lastUpdatedDate', header: 'AllocationConstraint.Column.lastUpdateDt', dataType: 'date', sort: 'true'},
      {field: 'lastUpdatedBy', header: 'AllocationConstraint.Column.lastUpdater', sort: 'true'},
    ]

    this.showTableColumns = this.tableColumns;

    this.searchForm = this.formBuilder.group({
      facility: null,
      storer: [null, Validators.required],
      brand: null,
      sku: null,
      checkTypes: [],
      activeFlag: null,
    })

    this.addForm = this.formBuilder.group({
      facility: null,
      storer: [null, Validators.required],
      sku: [null, Validators.required],
      brand: [null, Validators.required],
      checkType: ['DATE_CODE', Validators.required],
      checkCondition: ['>', Validators.required],
      checkValue1: [null, Validators.required],
      checkValue2: null,
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
    });

    this.pageInfo = {
      pageSize: 5,
      pageIndex: 0,
      totalRecords: 0,
      totalPages: 0,
    };

    this.sortInfo = null;
    this.commonOption = [
      { label: 'All', value: null },
      { label: 'Y', value: 'Y' },
      { label: 'N', value: 'N' }
    ];

  }

  async ngOnInit(): Promise<void>{
    await super.ngOnInit();
    // permission Codes
    this.permissionCodes = (
      Object.values(this.userProfile.roles).reduce(
        (c: any, i: any) => c.concat(i.permissions),
        []
      ) as any
    ).map((x: any) => {
      return x.permissionCode;
    });

    //rbac to maintain this page
    var canInPage = this.permissionCodes.includes(this.viewPermission);
    if (!canInPage) {
      //go unAuthorize
      this.routeStateService.navigateTo('/Error/unAuthorize');
    }
    this.canAddPermission = this.permissionCodes.includes(this.addPermission);

    this.DropdownOptionsService.getBrandOptions(1).subscribe({
      next: (options: any[]) => {
        this.brandOptions = options.map(x => ({
          label: x.brandName,
          value: x.brandName
        }));
        this.brandOptions.unshift({ label: 'ALL', value: 'ALL' });
      }
    });

    this.AddressMaintainanceService.getStorerOptions().subscribe({
      next: (result) => {
        this.storerOption = [...result].map((x: any) => {
          return {
            label: `${x.optionCode}`,
            value: x.optionCode,
          };
        }).sort((a, b) => a.label.localeCompare(b.label));
      },
    });

    this.checkTypeOption = this.getCheckConditionOptions();
    this.addCheckTypeOption = this.getCheckConditionOptions();

    this.addForm.get('checkType')?.valueChanges.subscribe((newCheckType) => {
      const options = this.getCurrentCheckConditionOptions(newCheckType);
      if (options.length > 0) {
        this.addForm.get('checkCondition')?.setValue(options[0].value);
      } else {
        this.addForm.get('checkCondition')?.setValue(null);
      }
    });
  }

  chipsAdd(event: any, fromControlName: any): void {
    const value: string = event.value;
    const parsedValues: string[] = value.split('\r\n').filter(x => isNullOrEmpty(x) === false && x !== '\r\n');
    const formControl = this.searchForm.get(fromControlName);
    formControl?.value.splice(formControl?.value.indexOf(value));
    formControl?.setValue(formControl?.value.concat(parsedValues));
  }


  putRowId(array: any[]) {
    let rowId = 0;
    for (const val of array) {
      val.rowId = rowId;
      rowId = rowId + 1;
    }
  }


  get selectedColumns(): any[] {
    return this.showTableColumns;
  }


  set selectedColumns(cols: any[]) {
    this.showTableColumns = this.tableColumns.filter(col => cols.includes(col));
  }


  get selectRow(): any {
    if (isNullOrEmpty(this.selectedRowId)) {
      return null;
    }
    return this.tableValue[this.selectedRowId];
  }

  set selectRow(rowData: any) {

    if (isNullOrEmpty(rowData)) {
      return;
    }

    if (isNullOrEmpty(this.selectedRowId)) {
      this.selectedRowId = rowData.rowId;
      return;
    }

    if (this.selectedRowId == rowData.rowId) {
      return;
    }

    this.selectedRowId = rowData.rowId;
  }

  searchBtnClick(event: any) {
    this.pageInfo.pageIndex = 0;

    if (this.checkInputValid()) {
      this.submitted = true;
      this.toastService.warn(this.translateService.instant('AllocationConstraint.Message.CheckInboundSearchInputValid'));
      return;
    }

    this.requestOfQuery();
  }

  checkInputValid(): boolean {
    if (isNullOrEmpty(this.searchForm.controls['storer'].value) ||
        isNullOrEmpty(this.searchForm.controls['brand'].value)) {
      return true;
    }
    return false;
  }

  tablePageChange(event: any) {
    this.pageInfo.pageIndex = event.first / this.pageInfo.pageSize;
    this.pageInfo.pageSize = event.rows;
    this.requestOfQuery();
  }

  tableSort(event: any): void {
    this.sortInfo = [{ property: event.field, direction: event.order }];
    this.requestOfQuery();
  }

  async setPageSize(event: any): Promise<void>{
    this.pageInfo.pageSize = event.rows;
  }

  requestOfQuery(isQuery: boolean = true) {
    const requestBody: AllocationConstraintInboundRequest = deepCopy(this.searchForm.value);
    requestBody.activeFlag = this.searchForm.value.activeFlag?.value;
    requestBody.facility = this.laasSetting.facility;
    if (this.searchParams.checkTypes === null) {
      requestBody.checkTypes = [];
    }
    requestBody.pageIndex = this.pageInfo.pageIndex;
    requestBody.pageSize = this.pageInfo.pageSize;
    requestBody.sortInfo = this.sortInfo;

    this.isLoading = true;

    if (isQuery) {
      // Clear selected row ID
      this.selectedRowId = undefined;

      this.DeliveryOrderService.queryAllocationInboundConstraint(requestBody)
          .subscribe({
            next: (result) => {
              const totalRecords = result.totalRecords;
              const flag = result.flag;

              if (result.flag === 'F') {
                this.tableValue = result.receiptConstraintDTOs || [];
                this.toastService.success(result.message);
              } else {
                this.toastService.warn(result.message);
                this.tableValue = [];
                this.pageInfo.totalRecords = 0;
              }
              // 共 ? 筆
              this.pageInfo.totalRecords = totalRecords;

              // Get data
              const receiptConstraintDTOs = result.receiptConstraintDTOs;

              this.tableValue = receiptConstraintDTOs.map((x: any) => {
                if (x.companyNameChinese && x.companyNameEnglish) {
                  x.customerName = x.companyNameChinese + '\n' + x.companyNameEnglish;
                } else {
                  x.customerName = x.companyNameChinese || x.companyNameEnglish || 'ALL';
                }
                return x;
              });

              // Initial table value and put row ID
              this.putRowId(this.tableValue);
            },
            error: (err) => {
              this.toastService.error(err.error);
              this.resetTable();
            },
            complete: () => {
              this.isLoading = false;
              this.isSearchFormOpen = false;
            }
          });

    }
  }

  openAddDialog(): void {
    this.showDialog = true;
  }

  closeAddDialog() {
    this.showDialog = false;

    this.addForm.reset();

    this.addForm.patchValue({
      checkType: 'DATE_CODE',
      checkCondition: '>'
    });

    this.startDateCalendar?.clearDate();
    this.endDateCalendar?.clearDate();

    this.addFormSubmitted = false;
  }

  markFormGroupDirty(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control) {
        control.markAsDirty();
      }
    });
  }

  saveConstraint() {
    this.addFormSubmitted = true;
    if (this.addForm.invalid) {
      this.addForm.markAllAsTouched();
      this.markFormGroupDirty(this.addForm);
      return;
    }
    if(this.validateAddForm() === false) {
      return;
    }

    const formValue = this.addForm.value;

    const requestBody: AddAllocationConstraintInboundRequest = {
      laasNo: null,
      recConstraintDTOs: []
    }
    const recConstraintDTOs = [
      {
        facility: this.laasSetting.facility,
        storer: formValue.storer,
        sku: formValue.sku,
        brand: formValue.brand,
        checkType: formValue.checkType,
        checkCondition: formValue.checkCondition,
        checkValue1: formValue.checkValue1,
        // 目前沒有between所以不會有值
        checkValue2: formValue.checkValue2,
        startDate: formValue.startDate,
        endDate: formValue.endDate,
        // 新增:固定帶 C
        crudFlag: 'C',
      }
    ];
    requestBody.recConstraintDTOs = recConstraintDTOs;

    this.DeliveryOrderService.addAllocationInboundConstraint(requestBody).subscribe({
      next: (res) => {
        this.toastService.success(res.message);
        this.closeAddDialog();
        },
      error: (err) => {
        this.toastService.error(err.message);
        this.closeAddDialog();
        console.error(err);
      }
    });
  }

  onCheckValue1(): void {
    const value = this.addForm.get('checkValue1')?.value;
    const regex = /^20\d{2}(0[1-9]|[1-4][0-9]|5[0-3])$/;
    this.checkValue1Invalid = value ? !regex.test(value) : false;

    if (this.checkValue1Invalid) {
      this.toastService.warn('請輸入正確格式：20YYWW，WW需為01-53，例如：202401');
    }
  }

  validateAddForm(): boolean {
    let isValid = true;
    if (this.addForm.get('startDate')?.value === null || this.addForm.get('startDate')?.value === undefined || this.addForm.get('startDate')?.value === '') {
      this.toastService.warn('生效日為必填');
      isValid = false;
    }
    if (this.addForm.get('endDate')?.value === null || this.addForm.get('endDate')?.value === undefined || this.addForm.get('endDate')?.value === '') {
      this.toastService.warn('失效日為必填');
      isValid = false;
    }
    // 檢查checkValue1格式
    if(this.checkValue1Invalid){
      isValid = false;
    }
    return isValid;
  }

  dateOnChange(dateObj: any, dateType: number) {
    // dateType 0: startDate, 1: endDate
    const dateValue = dateObj.date;
    if (!dateValue) return;

    let formattedDate = format(dateValue, 'yyyy-MM-dd');
    let startDateStr = this.addForm.controls.startDate.value;
    let endDateStr = this.addForm.controls.endDate.value;

    const newDate = formattedDate ? new Date(formattedDate) : null;
    const startDate = startDateStr ? new Date(startDateStr) : null;
    const endDate = endDateStr ? new Date(endDateStr) : null;

    // check validation
    // 如果輸入的是 startDate，並且大於 endDate → 互換
    if (dateType === 0 && endDate && newDate && newDate > endDate) {
      this.addForm.controls.startDate.setValue(endDateStr);
      this.addForm.controls.endDate.setValue(formattedDate);
      this.startDateCalendar.date = endDate;
      this.endDateCalendar.date = newDate;
      return;
    }
    // 如果輸入的是 endDate，並且小於 startDate → 互換
    if (dateType === 1 && startDate && newDate && newDate < startDate) {
      this.addForm.controls.endDate.setValue(startDateStr);
      this.addForm.controls.startDate.setValue(formattedDate);
      this.endDateCalendar.date = startDate;
      this.startDateCalendar.date = newDate;
      return;
    }
    // set value
    if (dateType == 0) {
      this.addForm.controls.startDate.setValue(formattedDate);
    } else {
      this.addForm.controls.endDate.setValue(formattedDate);
    }
  }

  resetTable(){
    this.tableValue = [];
    this.pageInfo = {
      pageSize: 5,
      pageIndex: 0,
      totalRecords: 0,
      totalPages: 0,
    };
  }

  getCurrentCheckConditionOptions(checkType: string): any[] {
    if (checkType && checkType in this.checkConditionOptionMap) {
      return this.checkConditionOptionMap[checkType as keyof typeof this.checkConditionOptionMap];
    }
    return [];
  }

  getCheckConditionOptions(){
    return Object.keys(this.checkConditionOptionMap).map((key) => {
      return { label: key, value: key };
    });
  }
}

